import { useMemo } from "react";
import {
    IssuedCredentialStatusEnum,
    useGetIssuedVerifiableQueryCredentialsQuery,
} from "@/src/graphql/generated/api/graphql";
import {
    Operator,
    VerifiableCredentialsFieldSelector,
} from "@/src/graphql/generated/api/graphql";
import { styled } from "./RevokedCredentialsDialog.styles";
import {
    Dialog,
    DialogActions,
    DialogContent,
    IconButton,
    Stack,
} from "@mui/material";
import { DialogTitle } from "../../molecules/DialogTitle/DialogTitle";
import CloseIcon from "@mui/icons-material/Close";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";

type RevokedCredentialsDialogProps = {
    subjectDid: string;
    subjectDidLabel?: string;
    open?: boolean;
    onClose?: () => void;
};

const RevokedCredentialsDialog = ({
    subjectDid,
    onClose,
    open = false,
    subjectDidLabel = "",
}: RevokedCredentialsDialogProps) => {
    const { t } = useTranslation("common");

    const { loading, data, error } =
        useGetIssuedVerifiableQueryCredentialsQuery({
            variables: {
                filters: [
                    {
                        selector: VerifiableCredentialsFieldSelector.SubjectId,
                        operator: Operator.In,
                        value: subjectDid,
                    },
                    {
                        selector: VerifiableCredentialsFieldSelector.Status,
                        operator: Operator.In,
                        value: IssuedCredentialStatusEnum.Revoked,
                    },
                ],
            },
            skip: !open,
            fetchPolicy: "no-cache",
        });

    const isFetched = loading && !data;
    const exchangeProfiles = useMemo(
        () =>
            data?.getSubjectsWithVerifiableQueryCredentials[0]
                ?.exchangeProfiles,
        [isFetched]
    );

    if (error) {
        return <p>{t("revokeditemsError")}</p>;
    }

    return (
        <Dialog
            onClose={onClose}
            open={open}
            maxWidth={"md"}
            fullWidth
            data-testid="revoked-credentials-dialog"
        >
            <DialogTitle>
                {t("validated-questions.revoked-dialog.title", {
                    subjectDid: subjectDidLabel,
                })}
                <IconButton color="inherit" onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    {!!exchangeProfiles &&
                        exchangeProfiles.map(
                            ({ name, version, credentials }) => {
                                const sortedCredentials = [...credentials].sort(
                                    (a, b) =>
                                        a.name.localeCompare(
                                            b.name,
                                            undefined,
                                            {
                                                numeric: true,
                                                sensitivity: "base",
                                            }
                                        )
                                );

                                return (
                                    <div
                                        key={`${subjectDid}-${version}-${name}`}
                                    >
                                        <h3>
                                            {name}
                                            <styled.ProfileVersion
                                                version={version}
                                            />
                                        </h3>
                                        {sortedCredentials.map(
                                            ({ name, id }, index) => (
                                                <Stack
                                                    key={id}
                                                    sx={{
                                                        ml: 3,
                                                        p: 2,
                                                        backgroundColor:
                                                            index % 2 === 0
                                                                ? "rgba(186, 42, 94, .05)"
                                                                : "",
                                                    }}
                                                    direction="row"
                                                    spacing={2}
                                                >
                                                    <p key={id}>{name}</p>
                                                </Stack>
                                            )
                                        )}
                                    </div>
                                );
                            }
                        )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>
                    {t("validated-questions.revoked-dialog.close")}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default RevokedCredentialsDialog;
